/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {NgModuleFactory, ɵisObservable as isObservable, ɵisPromise as isPromise} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {fromPromise} from 'rxjs/observable/fromPromise';
import {of } from 'rxjs/observable/of';
import {concatAll} from 'rxjs/operator/concatAll';
import {every} from 'rxjs/operator/every';
import * as l from 'rxjs/operator/last';
import {map} from 'rxjs/operator/map';
import {mergeAll} from 'rxjs/operator/mergeAll';
import {PRIMARY_OUTLET} from '../shared';
/**
 * @param {?} a
 * @param {?} b
 * @return {?}
 */
export function shallowEqualArrays(a: any[], b: any[]): boolean {
  if (a.length !== b.length) return false;
  for (let /** @type {?} */ i = 0; i < a.length; ++i) {
    if (!shallowEqual(a[i], b[i])) return false;
  }
  return true;
}
/**
 * @param {?} a
 * @param {?} b
 * @return {?}
 */
export function shallowEqual(a: {[x: string]: any}, b: {[x: string]: any}): boolean {
  const /** @type {?} */ k1 = Object.keys(a);
  const /** @type {?} */ k2 = Object.keys(b);
  if (k1.length != k2.length) {
    return false;
  }
  let /** @type {?} */ key: string;
  for (let /** @type {?} */ i = 0; i < k1.length; i++) {
    key = k1[i];
    if (a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}
/**
 * @template T
 * @param {?} arr
 * @return {?}
 */
export function flatten<T>(arr: T[][]): T[] {
  return Array.prototype.concat.apply([], arr);
}
/**
 * @template T
 * @param {?} a
 * @return {?}
 */
export function last<T>(a: T[]): T|null {
  return a.length > 0 ? a[a.length - 1] : null;
}
/**
 * @param {?} bools
 * @return {?}
 */
export function and(bools: boolean[]): boolean {
  return !bools.some(v => !v);
}
/**
 * @template K, V
 * @param {?} map
 * @param {?} callback
 * @return {?}
 */
export function forEach<K, V>(map: {[key: string]: V}, callback: (v: V, k: string) => void): void {
  for (const /** @type {?} */ prop in map) {
    if (map.hasOwnProperty(prop)) {
      callback(map[prop], prop);
    }
  }
}
/**
 * @template A, B
 * @param {?} obj
 * @param {?} fn
 * @return {?}
 */
export function waitForMap<A, B>(
    obj: {[k: string]: A}, fn: (k: string, a: A) => Observable<B>): Observable<{[k: string]: B}> {
  if (Object.keys(obj).length === 0) {
    return of ({});
  }

  const /** @type {?} */ waitHead: Observable<B>[] = [];
  const /** @type {?} */ waitTail: Observable<B>[] = [];
  const /** @type {?} */ res: {[k: string]: B} = {};

  forEach(obj, (a: A, k: string) => {
    const /** @type {?} */ mapped = map.call(fn(k, a), (r: B) => res[k] = r);
    if (k === PRIMARY_OUTLET) {
      waitHead.push(mapped);
    } else {
      waitTail.push(mapped);
    }
  });

  const /** @type {?} */ concat$ = concatAll.call(of (...waitHead, ...waitTail));
  const /** @type {?} */ last$ = l.last.call(concat$);
  return map.call(last$, () => res);
}
/**
 * @param {?} observables
 * @return {?}
 */
export function andObservables(observables: Observable<Observable<any>>): Observable<boolean> {
  const /** @type {?} */ merged$ = mergeAll.call(observables);
  return every.call(merged$, (result: any) => result === true);
}
/**
 * @template T
 * @param {?} value
 * @return {?}
 */
export function wrapIntoObservable<T>(value: T | NgModuleFactory<T>| Promise<T>| Observable<T>):
    Observable<T> {
  if (isObservable(value)) {
    return value;
  }

  if (isPromise(value)) {
    // Use `Promise.resolve()` to wrap promise-like instances.
    // Required ie when a Resolver returns a AngularJS `$q` promise to correctly trigger the
    // change detection.
    return fromPromise(Promise.resolve(value));
  }

  return of ( /** @type {?} */((value as T)));
}



// WEBPACK FOOTER //
// ../../../../packages/router/src/utils/collection.ts